import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ClientHeader } from '../components/client/ClientHeader';
import { LoadingCard, EmptyState, money, fmtDate, STATUS_COLOR } from '../components/client/clientShared';
import { Receipt, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientInvoicesPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_URL}/api/client/invoices`, { headers });
        setInvoices(res.data || []);
      } catch (err) {
        toast.error('Could not load your invoices');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const outstanding = invoices.reduce((s, i) => s + (['SENT', 'UNPAID', 'PARTIAL'].includes(i.status) ? (i.balanceCents || 0) : 0), 0);
  const paid = invoices.reduce((s, i) => s + (i.amountPaidCents || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader title="Invoices & Payments" subtitle="Invoices sent by your providers" backTo="/client/dashboard" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {!loading && invoices.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="text-2xl font-bold text-red-700" data-testid="invoices-outstanding">{money(outstanding)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">Total Paid</p>
              <p className="text-2xl font-bold text-green-700" data-testid="invoices-paid">{money(paid)}</p>
            </CardContent></Card>
          </div>
        )}

        {loading ? <LoadingCard /> : invoices.length === 0 ? (
          <EmptyState icon={<Receipt className="h-10 w-10 opacity-40" />} title="No invoices yet"
            subtitle="When a provider sends you an invoice, it will appear here." />
        ) : (
          <div className="space-y-3">
            {invoices.map((i) => (
              <Card key={i.id} data-testid={`invoice-${i.id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{i.invoiceNumber || 'Invoice'}</span>
                        <Badge className={STATUS_COLOR[i.status] || 'bg-gray-100 text-gray-700'}>{i.status}</Badge>
                      </div>
                      {i.providerName && <p className="text-sm mt-1">from {i.providerName}</p>}
                      {i.jobServiceName && <p className="text-xs text-muted-foreground">{i.jobServiceName}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {i.invoiceDate ? `Issued ${fmtDate(i.invoiceDate)}` : ''}{i.dueDate ? ` · Due ${fmtDate(i.dueDate)}` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{money(i.totalCents)}</p>
                      {i.balanceCents > 0 ? (
                        <p className="text-xs text-red-700">Balance {money(i.balanceCents)}</p>
                      ) : (
                        <p className="text-xs text-green-700">Paid</p>
                      )}
                    </div>
                  </div>
                  {i.publicToken && (
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate(`/invoice/${i.publicToken}`)} data-testid={`open-invoice-${i.id}`}>
                      <ExternalLink className="h-3 w-3 mr-1" /> View Invoice
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
